import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, message } from 'antd';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { toolsRestCrudThunksForSupplier } from '../../thunks/tools_thunks';
import DetailPageHeader from '../detail_page_header/DetailPageHeader';
import { getBackLinkStates } from '../../utils/HistoryUtils';
import { nullSafeGet, nullSafeGetOrElse, getCurrency } from '../../utils/DataAccessUtils';
import DeleteConfirmButton from '../delete_pop_confirm/DeletePopConfirm';
import AddToolModal from '../tools_index_page/AddToolModal';

const DEFAULT_BACK_LINK = '/supplier/tools';
const DEFAULT_BACK_LINK_TEXT = 'Back to Tools';

const ToolsDetailPage: FC<any> = ({
	location,
	match,
	getTool,
	history,
	tool,
	archiveTool,
	tools,
	companyConfig,
	childRoutes,
	toolsFetching,
}): React.ReactElement => {
	const currentUser = useSelector((state: any) => state.session.currentUser);
	const [deleting, setDeleting] = useState(false);
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [backLinkStates, setBackLinkStates] = useState<any>({
		backLinkText: DEFAULT_BACK_LINK_TEXT,
		backLinkTo: DEFAULT_BACK_LINK,
	});

	const toolId = match.params.id;

	useEffect(() => {
		const { backLinkText, backLinkTo } = getBackLinkStates(location, DEFAULT_BACK_LINK) as any;
		setBackLinkStates((existing) => ({
			...existing,
			...(backLinkText && { backLinkText }),
			...(backLinkTo && { backLinkTo }),
		}));
		getTool(toolId);
	}, [getTool, location, toolId]);

	const handleTabChange = useCallback(
		(key) => history.push(`/supplier/tools/detail/${toolId}/${key}`),
		[history, toolId]
	);

	const checkTabActive = useCallback(
		(key) => {
			const pathname = history.location.pathname;
			return pathname.indexOf(key) !== -1;
		},
		[history.location.pathname]
	);

	const handleDelete = useCallback(() => {
		setDeleting(true);
		archiveTool({ id: toolId })
			.then(() => {
				history.push('/supplier/tools');
			})
			.catch((err) => message.error(err))
			.finally(() => setDeleting(false));
	}, [archiveTool, history, toolId]);

	const currentTool = useMemo(
		() =>
			`${nullSafeGet('id', tool)}` === `${toolId}` ? tool : nullSafeGet(`records.${toolId}`, tools),
		[tool, toolId, tools]
	);

	const currency = useMemo(() => getCurrency({ currentUser }), [currentUser]);
	const currencyId = nullSafeGet('id', currency) || 'USD';

	const subtitle = useMemo(() => {
		const stockLocationName = nullSafeGetOrElse('stockLocation.name', currentTool, '');
		const costPerUse = nullSafeGet('costPerUse', currentTool);
		return (
			<span>
				{stockLocationName}
				{costPerUse ? `   ${costPerUse} ${currencyId}/use` : ''}
			</span>
		);
	}, [currentTool, currencyId]);

	const tabs = useMemo(() => [], []);

	return (
		<>
			<DetailPageHeader
				exactPath="/supplier/tools/detail/:id"
				redirectPath={`/supplier/tools/detail/${toolId}/details`}
				backLinkText={backLinkStates.backLinkText}
				backLinkTo={backLinkStates.backLinkTo}
				title={<span>{nullSafeGetOrElse('name', currentTool, '')}</span>}
				subtitle={subtitle}
				actions={[
					<Button
						key={1}
						ghost={true}
						loading={deleting}
						onClick={() => setEditModalVisible(true)}
						style={{ marginRight: 16 }}
					>
						Edit
					</Button>,
					<DeleteConfirmButton
						key={2}
						onConfirm={handleDelete}
						title="Are you sure you want to delete this tool?"
						disabled={toolsFetching}
					>
						<Button ghost={true} disabled={toolsFetching} loading={deleting}>
							Delete
						</Button>
					</DeleteConfirmButton>,
				]}
				tabs={tabs}
				backgroundColor={nullSafeGet('detail.config.theme.navblock', companyConfig)}
				handleTabChange={handleTabChange}
				checkTabActive={checkTabActive}
				childRoutes={childRoutes}
			/>
			{editModalVisible && (
				<AddToolModal
					visible
					tool={currentTool}
					onCancel={() => setEditModalVisible(false)}
					onSuccess={() => {
						setEditModalVisible(false);
						getTool(toolId);
					}}
				/>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	toolsFetching: state.tools.fetching,
	tools: state.tools,
	tool: state.tools.detail,
	companyConfig: state.company_config,
});

const mapDispatchToProps = (dispatch) => ({
	getTool: (id) => dispatch(toolsRestCrudThunksForSupplier.readOne(id)),
	archiveTool: (entity) =>
		dispatch(toolsRestCrudThunksForSupplier.delete(entity, 'id', null, true)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ToolsDetailPage));
